import { blobticMessage } from '../../Commun/components/BlobticMessage';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Grid, Card, CardContent, Chip,
  IconButton, TextField, MenuItem, Tooltip, LinearProgress,
  ToggleButton, ToggleButtonGroup, Stack
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Domain as DomainIcon, GridView as GridViewIcon, FormatListBulleted as TableListIcon,
  MeetingRoom as RoomIcon, ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { academyStore } from '../services/academyStore';
import { Room, RoomStatus } from '../types/academy';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import GenericPage, { useDefaultViewMode } from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import blobticService from '../../Commun/services/blobticService';

const ENTITY_KEY = 'Blobtic_Academy,academy_room';
const ACTION_SUFFIX = '';
const PRIMARY_KEY = 'ARM_IDF';

const fsx = {
  '& .MuiInputBase-root': { fontSize: '0.85rem' },
  '& .MuiInputLabel-root': { fontSize: '0.85rem' },
};

/** Map DB DTO row -> frontend Room object */
const dtoToRoom = (dto: any): Room => {
  const equipment = typeof dto.ARM_EQUIPMENT === 'string'
    ? dto.ARM_EQUIPMENT.split(',').map((s: string) => s.trim()).filter(Boolean)
    : (dto.ARM_EQUIPMENT || []);

  return {
    id: dto.ARM_IDF,
    room_number: dto.ARM_ROOM_NUMBER || '',
    floor: dto.ARM_FLOOR || '',
    capacity: Number(dto.ARM_CAPACITY || 20),
    equipment,
    status: dto.ARM_STATUS || 'Active',
    dos_idf: dto.DOS_IDF ? Number(dto.DOS_IDF) : undefined,
    dos_code: dto.DOS_CODE || undefined,
    dos_lib: dto.DOS_LIB || undefined,
    user_lib: dto.USER_LIB || undefined,
    last_updt: dto.LAST_UPDT
      ? new Date(dto.LAST_UPDT).toLocaleString('fr-DZ')
      : undefined,
  };
};

/** Map frontend Room object -> DB column names for Academy_Room_DM */
const roomToDto = (r: Partial<Room>): Record<string, any> => {
  const equipmentStr = Array.isArray(r.equipment)
    ? r.equipment.join(', ')
    : (r.equipment || '');

  return {
    ARM_IDF: (r as any).ARM_IDF ?? r.id,
    ARM_ROOM_NUMBER: r.room_number || '',
    ARM_FLOOR: r.floor || '',
    ARM_CAPACITY: r.capacity ?? 20,
    ARM_EQUIPMENT: equipmentStr,
    ARM_STATUS: r.status || 'Active',
    ...(r.dos_idf ? { DOS_IDF: r.dos_idf } : {}),
  };
};

const emptyRoom = (): Partial<Room> => ({
  room_number: '',
  floor: '1st Floor',
  capacity: 20,
  equipment: ['Projector', 'Whiteboard', 'WiFi'],
  status: 'Active',
});

export const RoomsPage: React.FC = () => {
  const { t } = useLanguage();
  

  // View state: 'list' (default) or 'form' (new/edit)
  const [view, setView] = useState<'list' | 'form'>('list');

  // Grid vs Table toggle for the list (initialized from user default preference)
  const [viewMode, setViewMode] = useDefaultViewMode();

  // Rooms list from academyStore
  const [roomsList, setRoomsList] = useState<Room[]>(() => academyStore.getRooms());
  const [listLoading, setListLoading] = useState(false);

  // Form State
  const [formData, setFormData] = useState<Partial<Room>>(emptyRoom());
  const [equipmentInput, setEquipmentInput] = useState<string>('Projector, Whiteboard, WiFi');

  // Store subscription for live updates from store
  useEffect(() => {
    academyStore.ensureRoomsLoaded();
    setRoomsList(academyStore.getRooms());
    return academyStore.subscribe(() => {
      setRoomsList(academyStore.getRooms());
    });
  }, []);

  // ── Navigation & Actions ───────────────────────────────────────────────────

  const openNew = () => {
    const initial = emptyRoom();
    setFormData(initial);
    setEquipmentInput(Array.isArray(initial.equipment) ? initial.equipment.join(', ') : '');
    setView('form');
  };

  const openEdit = (room: Room) => {
    setFormData({ ...room });
    setEquipmentInput(Array.isArray(room.equipment) ? room.equipment.join(', ') : '');
    setView('form');
  };

  const backToList = () => {
    setView('list');
    setFormData(emptyRoom());
  };

  // ── Field Setter ───────────────────────────────────────────────────────────

  const setField = <K extends keyof Room>(key: K, value: Room[K]) =>
    setFormData(prev => ({ ...prev, [key]: value }));

  const handleEquipmentChange = (value: string) => {
    setEquipmentInput(value);
    const list = value.split(',').map(e => e.trim()).filter(Boolean);
    setFormData(prev => ({ ...prev, equipment: list }));
  };

  // ── Form Validation & Callbacks ────────────────────────────────────────────

  const handleValidate = (): string | null => {
    if (!formData.room_number?.trim()) return "Le numéro de salle est obligatoire.";
    if (formData.capacity == null || formData.capacity <= 0) return "La capacité doit être un nombre supérieur à zéro.";
    if (!formData.status) return "Le statut de la salle est obligatoire.";
    return null;
  };

  // GenericPage auto-updates IndexedDB → BlobticDataManager notifies academyStore
  // → academyStore refreshes rooms[] → RoomsPage re-renders via subscribe().
  // Nothing to do here except navigate back.
  const handleDataChange = () => backToList();

  const handleClear = () => backToList();

  // ── Delete ─────────────────────────────────────────────────────────────────

  const handleDeleteFromList = async (room: Room) => {
    // boubaker 23/08/2026 - confirmation via le composant générique animé
    if (!(await blobticMessage.confirm({
      title: t('common.confirm.title.delete'),
      message: t('academy.rooms.confirm_delete'),
      severity: 'warning',
      confirmText: t('common.actions.confirm'),
      cancelText: t('btn.cancel')
    }))) return;
    try {
      const res = await academyStore.archiveRoom(room.id);
      if (res.success) {
        blobticMessage.success(t('common.messages.success'), t('common.messages.deleted_success'));
      } else {
        blobticMessage.error(t('common.error'), res.message || 'Erreur lors de la suppression.');
      }
    } catch (e: any) {
      blobticMessage.error('Erreur', e.message);
    }
  };

  // ─── LIST VIEW ──────────────────────────────────────────────────────────────

  if (view === 'list') {
    return (
      <GenericPage
        title={t('academy.rooms.title')}
        icon={<DomainIcon />}
        footerEnabled={false}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onNew={openNew}
        newButtonLabel={t('academy.rooms.add_new')}
      >
        {listLoading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">Chargement en cours...</Typography>
          </Box>
        ) : viewMode === 'grid' ? (
          /* ── GRID VIEW ── */
          <Grid container spacing={2.5}>
            {roomsList.map(room => {
              const util = academyStore.getRoomUtilizationPercentage(room.id);
              const isHighCap = room.capacity >= 25;

              return (
                <Grid item xs={12} sm={6} md={4} key={room.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 3,
                      borderColor: room.status === 'Active' ? 'divider' : 'warning.light',
                      bgcolor: room.status === 'Active' ? 'background.paper' : 'warning.50',
                      transition: 'transform 0.2s',
                      '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' }
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <RoomIcon color="primary" />
                          <Typography variant="h6" fontWeight="bold">
                            {t('academy.rooms.room_prefix', room.room_number)}
                          </Typography>
                        </Box>
                        <Chip
                          label={
                            room.status === 'Active'
                              ? t('academy.rooms.status.active')
                              : room.status === 'Under Maintenance'
                                ? t('academy.rooms.status.maintenance')
                                : t('academy.rooms.status.archived')
                          }
                          size="small"
                          color={
                            room.status === 'Active'
                              ? 'success'
                              : room.status === 'Under Maintenance'
                                ? 'warning'
                                : 'default'
                          }
                        />
                      </Box>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {t('academy.rooms.location')} {room.floor || t('academy.common.unassigned_floor')}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Chip
                          label={t('academy.rooms.capacity_label', room.capacity)}
                          color={isHighCap ? 'secondary' : 'default'}
                          size="small"
                          variant="outlined"
                        />
                      </Box>

                      {/* Utilization Bar */}
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">{t('academy.rooms.weekly_util')}</Typography>
                          <Typography variant="caption" fontWeight="bold">{util}%</Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={util}
                          color={util > 80 ? 'error' : util > 50 ? 'warning' : 'primary'}
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </Box>

                      {/* Equipment Tags */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                        {(room.equipment || []).map((item, idx) => (
                          <Chip key={idx} label={item} size="small" variant="filled" sx={{ fontSize: '0.7rem' }} />
                        ))}
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <IconButton size="small" color="primary" onClick={() => openEdit(room)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteFromList(room)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          /* ── TABLE VIEW ── */
          <GenericTable
            columns={[
              { id: 'room_number', label: t('academy.rooms.room_number'), minWidth: 150 },
              { id: 'floor', label: t('academy.rooms.floor'), minWidth: 140 },
              { id: 'capacity', label: t('academy.rooms.capacity'), minWidth: 120, format: (val: any) => t('academy.rooms.capacity_label', val) },
              {
                id: 'utilization',
                label: t('academy.rooms.weekly_util'),
                minWidth: 160,
                format: (_: any, row: any) => {
                  const util = academyStore.getRoomUtilizationPercentage(row.id);
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight="bold">{util}%</Typography>
                      <LinearProgress variant="determinate" value={util} sx={{ width: 60, height: 6, borderRadius: 3 }} />
                    </Box>
                  );
                }
              },
              {
                id: 'equipment',
                label: t('academy.rooms.equipment'),
                minWidth: 200,
                format: (val: any) => (Array.isArray(val) ? val.map((eq: string, i: number) => <Chip key={i} label={eq} size="small" sx={{ mr: 0.5 }} />) : val)
              },
              {
                id: 'status',
                label: t('academy.rooms.status'),
                minWidth: 140,
                format: (val: any) => (
                  <Chip
                    label={
                      val === 'Active'
                        ? t('academy.rooms.status.active')
                        : val === 'Under Maintenance'
                          ? t('academy.rooms.status.maintenance')
                          : t('academy.rooms.status.archived')
                    }
                    size="small"
                    color={val === 'Active' ? 'success' : val === 'Under Maintenance' ? 'warning' : 'default'}
                  />
                )
              }
            ]}
            data={roomsList}
            onEdit={(row) => openEdit(roomsList.find(r => r.id === row.id)!)}
            onDelete={(row) => handleDeleteFromList(roomsList.find(r => r.id === row.id)!)}
            filterColumns={['status', 'floor']}
            searchPlaceholder={t('academy.rooms.search_placeholder')}
            maxHeight="calc(100vh - 380px)"
          />
        )}
      </GenericPage>
    );
  }

  // ─── FORM VIEW (New / Edit) ────────────────────────────────────────────────

  const isEditing = !!(formData as any).ARM_IDF || !!formData.id;
  const formPayload = roomToDto(formData);

  return (
    <GenericPage
      title={
        isEditing
          ? t('academy.rooms.form.title_edit', formData.room_number)
          : t('academy.rooms.form.title_new')
      }
      icon={<DomainIcon />}
      footerEnabled={true}
      footerInfo={{
        lastUpdate: formData.last_updt || '-',
        userLib: formData.user_lib || '-',
      }}
      additionalActions={
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={backToList}
        >
          {t('academy.rooms.back_to_list')}
        </Button>
      }
      crudConfig={{
        entityKey: ENTITY_KEY,
        actionSuffix: ACTION_SUFFIX,
        primaryKey: PRIMARY_KEY,
      }}
      data={formPayload}
      onDataChange={() => handleDataChange()}
      onValidate={handleValidate}
      onClear={handleClear}
    >
      <Box sx={{ maxWidth: 800, mx: 'auto', pb: 4 }}>

        {/* ── Main Room Details Form ── */}
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {t('academy.rooms.section_info')}
          </Typography>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('academy.rooms.modal.number')}
                size="small"
                fullWidth
                required
                sx={fsx}
                value={formData.room_number ?? ''}
                onChange={e => setField('room_number', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('academy.rooms.modal.floor')}
                size="small"
                fullWidth
                sx={fsx}
                value={formData.floor ?? ''}
                onChange={e => setField('floor', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('academy.rooms.modal.capacity')}
                type="number"
                size="small"
                fullWidth
                required
                sx={fsx}
                value={formData.capacity ?? 20}
                onChange={e => setField('capacity', Number(e.target.value))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label={t('academy.rooms.status')}
                size="small"
                fullWidth
                required
                sx={fsx}
                value={formData.status ?? 'Active'}
                onChange={e => setField('status', e.target.value as RoomStatus)}
              >
                <MenuItem value="Active">{t('academy.rooms.status.active')}</MenuItem>
                <MenuItem value="Under Maintenance">{t('academy.rooms.status.maintenance')}</MenuItem>
                <MenuItem value="Archived">{t('academy.rooms.status.archived')}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t('academy.rooms.modal.equipment')}
                size="small"
                fullWidth
                sx={fsx}
                value={equipmentInput}
                onChange={e => handleEquipmentChange(e.target.value)}
                helperText={t('academy.rooms.modal.equipment_helper')}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* ── Dossier Information ── */}
        {(formData.dos_code || formData.dos_lib) && (
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
              {t('academy.rooms.section_dossier')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('academy.rooms.form.dossier_code')}
                  size="small"
                  fullWidth
                  disabled
                  sx={fsx}
                  value={formData.dos_code ?? ''}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('academy.rooms.form.dossier_lib')}
                  size="small"
                  fullWidth
                  disabled
                  sx={fsx}
                  value={formData.dos_lib ?? ''}
                />
              </Grid>
            </Grid>
          </Paper>
        )}

      </Box>
    </GenericPage>
  );
};

export default RoomsPage;
